import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";

//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider
} from "@mui/material";
import { Button, Checkbox } from "@gpstrackit/react-controls"
import { getDateFormatted, getPMUser } from 'Core/data/Helpers';

//Views
import DataView from 'Components/DataView';
import CreateEditEvent from 'Modules/events/views/.components/createEditEvent';
import DeleteEvent from 'Modules/events/views/.components/deleteEvent';
import { NotificationManager } from 'Components/Notifications';



//Security
import { HasPermission } from "Core/security";

//Actions
import {
    eventCreateEditOpenModal,
    eventDeleteOpenModal,
    searchEntityRequest,
} from "Redux/actions"

const EventsMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);
    const pmUsers = useSelector(state => state.generalRedux.pmUsers);
    const deleteManuOpenModal = useSelector(state => state.deleteEventRedux.openModal);
    const createEditManuOpenModal = useSelector(state => state.createEditEventRedux.openModal);
    const lang = useSelector(state => state.commonRedux.language);

    const [columns, setColumns] = useState([
        {
            key: "id",
            title: messages['events_main_id'],
            width: 25,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "events_main_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            field: "name",
            title: messages['events_main_name'],
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.name',
            code: "events_main_name",
            render: (item) => {
                return (
                    <span style={{ color: `${item?.color || ""}` }}>{item?.name}</span>
                )
            }
        },
        {
            key: "diagnostic",
            field: "diagnostic",
            title: messages['events_main_diagnostic'],
            show: true,
            sort: true,
            locked: false,
            export: true,
            functionString: `
                let lang = "${lang}"
                if(lang === "en" && row.diagnostic) {
                    return "Yes"
                }
                if(lang === "en" && !row.diagnostic) {
                    return "No"
                }

                if(lang === "es" && row.diagnostic) {
                    return "Si"
                }
                if(lang === "es" && !row.diagnostic) {
                    return "No"
                }
            `,
            code: "events_main_diagnostic",
            width: 150,
            render: (item) => {
                return (
                    <Checkbox
                        id={`${item.id}_diagnostic`}
                        value={item.diagnostic}
                        disabled={true}
                    />
                )
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['events_main_created_on'],
            sort: true,
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date) {
                const options = {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric',
                  hour12: false,
                  timeZone: "UTC"
                };
                let currentDate = new Intl.DateTimeFormat('es-US', options).format(new Date(date));
                return currentDate.replace(/,/g, '').replace(/GMT/g, "");
              }

              return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "devices_main_createdOn",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: 'createdBy',
            show: true,
            title: messages["events_main_created_by"],
            width: 200,
            field: "createdBy",
            export: true,
            resize: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
            `,
            code: "events_main_created_by",
            render: (item) => {
                return <span>{item?.createdBy && getPMUser(item?.createdBy, pmUsers)}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['events_main_updated_on'],
            sort: true,
            width: 250,
            show: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
            function formatDate(date) {
                const options = {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric',
                  hour12: false,
                  timeZone: "UTC"
                };
                let currentDate = new Intl.DateTimeFormat('es-US', options).format(new Date(date));
                return currentDate.replace(/,/g, '').replace(/GMT/g, "");
              }

              return row?.updatedOn ? formatDate(row?.updatedOn) : ''
            `,
            code: "devices_main_updatedOn",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime", null, item?.timezone)}</span>
            }
        },
        {
            key: 'updatedBy',
            show: true,
            title: messages["events_main_updated_by"],
            width: 200,
            field: "updatedBy",
            export: true,
            resize: true,
            functionString: `
                let pmUsers = ${JSON.stringify(pmUsers)}
                pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
            `,
            code: "events_main_updated_by",
            render: (item) => {
                return <span>{item?.updatedBy && getPMUser(item?.updatedBy, pmUsers)}</span>
            }
        },
    ])
    const [searchFields, setSearchFields] = useState(["name"]);
    const [openPanel, setOpenPanel] = useState(false);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        if (pmUsers) {
            let currentColumns = columns.map((item) => ({
                ...item,
                title: messages[item.code],
                render: item.key === "diagnostic" ? (item) => {
                    return (
                        <Checkbox
                            id={`${item.id}_diagnostic`}
                            value={item.diagnostic}
                            disabled={true}
                        />
                    )
                } : item.key === "createdBy" ? (item) => {
                    let label = item?.createdBy ? getPMUser(item?.createdBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.key === "updatedBy" ? (item) => {
                    let label = item?.updatedBy ? getPMUser(item?.updatedBy, pmUsers) : ""
                    return <span>{label}</span>
                } : item.render,
                functionString: item.key === "diagnostic" ? `
                    let lang = "${lang}"
                    if(lang === "en" && row.diagnostic) {
                        return "Yes"
                    }
                    if(lang === "en" && !row.diagnostic) {
                        return "No"
                    }

                    if(lang === "es" && row.diagnostic) {
                        return "Si"
                    }
                    if(lang === "es" && !row.diagnostic) {
                        return "No"
                    }
                `
                    : item.key === "createdBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.createdBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.createdBy
                ` : item.key === "updatedBy" ? `
                    let pmUsers = ${JSON.stringify(pmUsers)}
                    pmUsers = pmUsers.find((item) => ("PM_"+item.id) === row?.updatedBy)
                    return pmUsers.firstName + " " + pmUsers.lastName || row?.updatedBy
                ` : item.functionString
            }))
            setColumns([...currentColumns])
        }
    }, [pmUsers, lang])

    useEffect(() => {
        dispatch(searchEntityRequest({ key: "pmUsers" }));
    }, []);

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['events_main_title']} ({total})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons">
                        <Button
                            disabled={!HasPermission(user, "evnt", "c")}
                            className="btn-dark-blue"
                            onClick={(e) => {
                                dispatch(eventCreateEditOpenModal(true))
                                e.preventDefault();
                            }}
                        >
                            {messages['events_main_add_event']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        exportName={messages['events_main_title']}
                        dinamicQuery
                        name="pm-events"
                        entity="events"
                        graphqlFunction="searchEvents"
                        graphqlSort="EventSortInput"
                        graphqlConditions="EventSearchInput"
                        customQuery="id name color diagnostic"
                        columns={columns}
                        hideAllInPagination
                        asyncSelect
                        searchConditions={["name"]}
                        searchOptionValue="id"
                        searchOptionLabel="name"
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags;
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                            }

                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name');

                            let tagsById = currentTags.filter((item) => typeof item.id === "number");
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item.name))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                            else tableRef?.current?.getData(null, rows, sort, currentConditions);
                        }}
                        searchFields={searchFields}
                        topheight={200}
                        onChangeTotal={(total) => setTotal(total)}
                        onFilters={() => { setOpenPanel(!openPanel) }}
                        noSplit
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={["export", 'orderColumn']}
                        noFilters
                        options={[
                            {
                                id: "edit-event",
                                label: messages['edit'],
                                single: true,
                                show: true,
                                disabled: !HasPermission(user, "evnt", "u"),
                                action: (items) => {
                                    dispatch(eventCreateEditOpenModal(true, items[0]))
                                }
                            },
                            {
                                id: "delete-event",
                                label: messages['delete'],
                                single: true,
                                show: true,
                                disabled: !HasPermission(user, "evnt", "d"),
                                action: (items) => {
                                    dispatch(eventDeleteOpenModal(true, items[0]))
                                }
                            }
                        ]}
                        menuOptions={(row) => {
                            let actions = [
                                messages['edit'],
                                messages['delete']
                            ]
                            return actions
                        }}
                        onClickMenu={(event, item) => {
                            if (event === messages['edit']) {
                                if (HasPermission(user, "evnt", "u")) {
                                    dispatch(eventCreateEditOpenModal(true, item))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['events_edit_title'],
                                        5000,
                                    );
                                }
                            }
                            if (event === messages['delete']) {
                                if (HasPermission(user, "evnt", "d")) {
                                    dispatch(eventDeleteOpenModal(true, item))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['events_delete_title'],
                                        5000,
                                    );
                                }
                            }
                        }}
                    />
                </Grid>
            </Paper>
            {createEditManuOpenModal && <CreateEditEvent tableRef={tableRef} />}
            {deleteManuOpenModal && <DeleteEvent tableRef={tableRef} />}
        </Container>
    )
}
export default EventsMain;